// 2.D.7 + 2.F.4 — Projektin detail-naekyma.
//
// Nayttaa: nimi, status, members + lista projektin asseteista. 2.F.4 lisasi
// "Arkistoi"-painikkeen omistajalle/manageri:lle ja "Vie pois" -modaalin
// arkistoidulle projektille (Kopia-komennot manuaali-restorelle).

import { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "wouter";
import { apiClient, ApiError, NotAuthenticatedError } from "../api/client.js";
import { AssetGrid } from "../components/asset-grid.js";
import { ConfirmDialog } from "../components/confirm-dialog.js";
import { ForkProjectDialog } from "../components/fork-project-dialog.js";
import { SidecarAttachModal } from "../components/sidecar-attach-modal.js";
import type { Asset, ProjectDetailResponse } from "../api/types.js";

export function ProjectDetail() {
  const params = useParams<{ id: string }>();
  const id = params.id;
  const [, navigate] = useLocation();

  const [data, setData] = useState<ProjectDetailResponse | null>(null);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setLoading] = useState(true);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [archiveProcessing, setArchiveProcessing] = useState(false);
  const [exportInstructions, setExportInstructions] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [showSidecarModal, setShowSidecarModal] = useState(false);
  const [showForkDialog, setShowForkDialog] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      apiClient.getProject(id),
      apiClient.listAssets({ projectId: id, limit: 200 }),
    ])
      .then(([proj, assetList]) => {
        if (cancelled) return;
        setData(proj);
        setAssets(assetList.assets);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof NotAuthenticatedError) navigate("/login");
        else if (err instanceof ApiError) setError(err.message);
        else setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  if (!id) return null;

  async function archive() {
    if (!id) return;
    setArchiveProcessing(true);
    try {
      await apiClient.archiveProject(id);
      // Lataa projekti uudelleen jotta status nakyy
      const proj = await apiClient.getProject(id);
      setData(proj);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setArchiveProcessing(false);
      setConfirmArchive(false);
    }
  }

  async function loadExportInstructions() {
    if (!id) return;
    setExportLoading(true);
    setExportError(null);
    try {
      const res = await apiClient.getExportInstructions(id);
      setExportInstructions(res.instructions);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : String(err));
    } finally {
      setExportLoading(false);
    }
  }

  const isOwnerOrManager = data && (data.access === "owner" || data.access === "editor");
  const isArchived = data?.project.lifecycleState === "archived";

  return (
    <section className="mx-auto max-w-6xl px-6 py-8">
      <Link
        href="/projektit"
        className="mb-4 inline-block text-sm text-slate-400 hover:text-slate-200"
      >
        ← Projektit
      </Link>

      {isLoading && <p className="text-slate-500">Ladataan…</p>}
      {error && (
        <div className="rounded-md border border-red-700 bg-red-950/40 px-4 py-3 text-sm text-red-200">
          {error}
        </div>
      )}

      {data && (
        <>
          <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="text-2xl font-semibold tracking-tight text-slate-100">
                {data.project.name}
              </h2>
              <p className="mt-2 flex flex-wrap items-center gap-3 text-sm text-slate-400">
                {data.project.status && (
                  <span className="rounded-full border border-slate-700 px-3 py-0.5">
                    Status: {data.project.status}
                  </span>
                )}
                {isArchived && (
                  <span className="rounded-full border border-amber-700 bg-amber-950/40 px-3 py-0.5 text-amber-200">
                    Arkistoitu
                  </span>
                )}
                <span className="text-slate-500">
                  Sinun roolisi: <strong>{data.access}</strong>
                </span>
                <span className="text-slate-500">
                  {assets.length} asset
                  {assets.length === 1 ? "" : "ia"}
                </span>
              </p>
            </div>
            <div className="flex gap-2">
              {!isArchived && isOwnerOrManager && (
                <button
                  type="button"
                  onClick={() => setShowSidecarModal(true)}
                  className="rounded-md border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-800"
                  title="Liitä kansio tälle projektille kirjoittamalla .kratti-project.yaml"
                >
                  Liitä kansio
                </button>
              )}
              {!isArchived && isOwnerOrManager && (
                <button
                  type="button"
                  onClick={() => setConfirmArchive(true)}
                  className="rounded-md border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-800"
                >
                  Arkistoi projekti
                </button>
              )}
              {isArchived && (
                <button
                  type="button"
                  onClick={loadExportInstructions}
                  disabled={exportLoading}
                  className="rounded-md bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-900 hover:bg-white disabled:opacity-50"
                >
                  {exportLoading ? "Ladataan…" : "Vie pois (Kopia-komennot)"}
                </button>
              )}
              {isOwnerOrManager && (
                <button
                  type="button"
                  onClick={() => setShowForkDialog(true)}
                  className="rounded-md border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-800"
                  title="Luo uusi projekti tämän pohjalta — uusi id, ei assetteja"
                >
                  Käytä pohjana
                </button>
              )}
            </div>
          </header>

          {data.members.length > 0 && (
            <section className="mb-6">
              <h3 className="text-sm font-medium uppercase tracking-wide text-slate-500">
                Jasenet
              </h3>
              <ul className="mt-2 flex flex-wrap gap-2">
                {data.members.map((m) => (
                  <li
                    key={m.userId}
                    className="rounded-full border border-slate-700 bg-slate-900 px-3 py-1 text-sm text-slate-200"
                  >
                    {m.name ?? m.email ?? m.userId} · {m.access}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section>
            <h3 className="mb-3 text-sm font-medium uppercase tracking-wide text-slate-500">
              Assetit
            </h3>
            {assets.length === 0 ? (
              <p className="text-slate-500">
                Projektissa ei ole vielae asseteja.{" "}
                <Link href="/selaa" className="underline hover:text-slate-200">
                  Selaa-naekymassa
                </Link>{" "}
                voit valita asseteja ja siirta ne tahan projektiin bulk-actionilla
                (move-to-project).
              </p>
            ) : (
              <AssetGrid assets={assets} />
            )}
          </section>

          {showSidecarModal && data?.project && (
            <SidecarAttachModal
              project={data.project}
              onClose={() => setShowSidecarModal(false)}
            />
          )}

          {data?.project && (
            <ForkProjectDialog
              open={showForkDialog}
              source={data.project}
              onClose={() => setShowForkDialog(false)}
              onCreated={(newProject) => {
                navigate(`/projektit/${newProject.id}`);
              }}
            />
          )}

          <ConfirmDialog
            open={confirmArchive}
            title="Arkistoi projekti?"
            message={
              <p>
                Projekti merkitaan arkistoiduksi. Assetit pysyvat ennallaan, mutta
                projektin tilaksi tulee <strong>archived</strong>. Voit milloin tahansa
                pyytaa export-Kopia-komentoja palauttaaksesi tiedostot Macille.
              </p>
            }
            confirmLabel="Arkistoi"
            isProcessing={archiveProcessing}
            onConfirm={archive}
            onCancel={() => setConfirmArchive(false)}
          />

          {exportInstructions !== null && (
            <ExportInstructionsModal
              instructions={exportInstructions}
              onClose={() => setExportInstructions(null)}
            />
          )}
          {exportError && (
            <div className="fixed bottom-4 right-4 max-w-md rounded-md border border-red-700 bg-red-950/80 px-4 py-3 text-sm text-red-200">
              Export epaonnistui: {exportError}
              <button
                type="button"
                onClick={() => setExportError(null)}
                className="ml-2 underline"
              >
                Sulje
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function ExportInstructionsModal({
  instructions,
  onClose,
}: {
  instructions: string;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-slate-950/80 px-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-3xl rounded-lg border border-slate-800 bg-slate-950 p-6 shadow-2xl">
        <header className="mb-4 flex items-start justify-between gap-4">
          <h3 className="text-lg font-semibold tracking-tight text-slate-100">
            Vie pois — Kopia-komennot
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-2 py-1 text-slate-400 hover:bg-slate-900 hover:text-slate-100"
            aria-label="Sulje"
          >
            ✕
          </button>
        </header>
        <p className="mb-3 text-sm text-slate-400">
          Kopioi nama komennot terminaaliin Macilla. Vaatii kopia-CLI:n ja SOPS-passphrase:n.
        </p>
        <pre className="overflow-x-auto rounded-md border border-slate-800 bg-slate-900 p-4 text-xs text-slate-100">
          {instructions}
        </pre>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => navigator.clipboard.writeText(instructions)}
            className="rounded-md border border-slate-700 bg-slate-900 px-4 py-2 text-sm text-slate-200 hover:bg-slate-800"
          >
            Kopioi
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md bg-slate-100 px-4 py-2 text-sm font-medium text-slate-900 hover:bg-white"
          >
            Sulje
          </button>
        </div>
      </div>
    </div>
  );
}
